import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { environment } from '@env/environment';
import { BehaviorSubject, catchError, EMPTY, filter, switchMap, take, throwError } from 'rxjs';
import { AuthService, UserData } from '../services/auth.service';

const refreshingTokenSubject = new BehaviorSubject<boolean | null>(null);
let refreshingToken = false;

export const authInterceptor: HttpInterceptorFn = (request, next) => {
  const authService = inject(AuthService);
  const modifiedRequest: HttpRequest<any> = addAuthorization(request, authService);

  return next(modifiedRequest).pipe(
    catchError((error) => {
      if (error.status === 0 && error.error instanceof ProgressEvent) {
        console.error({
          title: 'Error - Cannot connect to the server',
          message: 'Please check your internet connection and try again',
        });

        return EMPTY;
      }

      if (!(error instanceof HttpErrorResponse)) return EMPTY;

      if (request.url.includes('refresh')) {
        authService.logout();
        return EMPTY;
      }

      if (error.status !== 440) {
        console.error(error);
        if (request.responseType === 'blob' && error.error.type === 'application/json') {
          Promise.resolve(error).then(async (x) => {
            console.error({
              data: {
                title: `Error [${error?.status}]`,
                message:
                  JSON.parse(await x.error.text()).detail ||
                  error?.error?.detail ||
                  JSON.stringify(error?.error ? error?.error : error),
              },
            });
          });
        } else {
          console.error({
            data: {
              title: `Error [${error?.status}]`,
              message: error?.error?.detail || JSON.stringify(error?.error ? error?.error : error),
            },
          });
        }

        return throwError(() => error);
      }

      if (refreshingToken) {
        return refreshingTokenSubject.pipe(
          filter((refreshingTokenSubject: boolean | null) => refreshingTokenSubject !== null),
          take(1),
          switchMap(() => next(addAuthorization(request, authService)))
        );
      } else {
        refreshingToken = true;
        refreshingTokenSubject.next(null);

        return authService.refreshToken().pipe(
          switchMap(() => {
            refreshingToken = false;
            refreshingTokenSubject.next(true);
            return next(addAuthorization(request, authService));
          })
        );
      }
    })
  );
};

function addAuthorization(request: HttpRequest<any>, authService: AuthService): HttpRequest<any> {
  if (request.url.startsWith('/')) {
    request = request.clone({ url: `${environment.API_URL}${request.url}` });
  }

  if (
    request.url.includes('login') ||
    request.url.includes('refresh') ||
    request.url.endsWith('amazonaws.com/') ||
    (request.url.includes('amazonaws.com/') && !request.url.includes('/pre-sign-'))
  ) {
    return request;
  }

  // always adding device id to the request headers
  request = request.clone({
    headers: request.headers.set('Device-Id', authService.getDeviceId()),
  });

  const user: UserData | null = authService.getUserFromLocalStorage();
  if (!user) {
    return request;
  }

  const accessToken: string = user?._accessToken;
  if (!accessToken) {
    return request;
  }

  return request.clone({
    headers: request.headers
      .set('Authorization', `Bearer ${accessToken}`)
      .set('Content-Type', 'application/json; charset=utf-8'),
  });
}
